/**
 * Formulário de criação de assistência (ASM/AST/ASP) — wizard mobile first.
 * Uma pergunta por passo (ver Wizard.jsx + wizardSteps.jsx); este componente
 * é o dono do ESTADO (cabeçalho + peças), do rascunho local e do envio.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import {
  criarAssistencia,
  listarClientesSelect,
  listarFitasSelect,
  listarMateriaisSelect,
} from "../../../services/solicitacoes";
import Wizard from "./Wizard";
import { passosAssistencia } from "./wizardSteps";
import { carregarRascunho, limparRascunho, useAutosaveRascunho } from "./rascunho";
import {
  contarBordasComFita, erroDetalheDoMotivo, formatarCorMdf, gerarUid,
  parseDecimal, registroIntocado, unwrapItem, unwrapLista, validarMedidaMdf,
} from "./utils";
import { BannerRascunho } from "./styles";

function novaPeca() {
  return {
    uid: gerarUid(),
    ambiente: "",
    modulo: "",
    ref_modulo: "",
    descricao: "",
    natureza: "NOVA",
    material_id: null,
    material_nome: "",
    material_codigo: "",
    comprimento_mm: "",
    largura_mm: "",
    espessura_mm: "",
    observacao: "",
    quantidade: "1",
    fita_frente: false,
    fita_traseira: false,
    fita_le: false,
    fita_ld: false,
    fita_nome: "",
    usinagem: false,
    usinagem_descricao: "",
    sentido_veio: false,
    pintura: false,
    pintura_descricao: "",
    motivo_categoria: "",
    motivo_detalhe: "",
    fotos: [],
  };
}

function cabecalhoVazio() {
  return {
    num_pedido: "",
    cliente: null,
    clienteTexto: "",
    supervisor_nome: "",
    lider_montagem_nome: "",
    setor: "",
    lote_producao: "",
  };
}

export default function CriarAssistencia({ tipoInfo }) {
  const tipo = tipoInfo.value;
  const navigate = useNavigate();
  const { request } = useAuth();
  const toast = useToast();

  const rascunhoInicial = useMemo(() => carregarRascunho(tipo), [tipo]);
  const [cab, setCab] = useState(() => ({
    ...cabecalhoVazio(),
    ...(rascunhoInicial?.cab || {}),
  }));
  const [pecas, setPecas] = useState(() =>
    Array.isArray(rascunhoInicial?.pecas) && rascunhoInicial.pecas.length
      ? rascunhoInicial.pecas.map((p) => ({ ...novaPeca(), ...p, uid: gerarUid(), fotos: [] }))
      : [novaPeca()],
  );
  const [mostrarBannerRascunho, setMostrarBannerRascunho] = useState(!!rascunhoInicial);
  const [sujo, setSujo] = useState(false);
  const [erros, setErros] = useState({});

  const [clientes, setClientes] = useState([]);
  const [clientesCarregando, setClientesCarregando] = useState(true);
  const [materiais, setMateriais] = useState([]);
  const [materiaisCarregando, setMateriaisCarregando] = useState(true);
  const [fitas, setFitas] = useState([]);
  const [fitasCarregando, setFitasCarregando] = useState(true);

  useAutosaveRascunho(tipo, { cab, pecas }, sujo);

  useEffect(() => {
    let cancelado = false;
    listarClientesSelect(request)
      .then((payload) => {
        if (cancelado) return;
        setClientes(
          unwrapLista(payload).map((c) => ({
            id: c.id,
            nome: c.nome,
            label: c.nome,
            sublabel: "",
          })),
        );
      })
      .catch(() => {})
      .finally(() => !cancelado && setClientesCarregando(false));

    listarMateriaisSelect(request)
      .then((payload) => {
        if (cancelado) return;
        setMateriais(
          unwrapLista(payload).map((m) => ({
            id: m.id,
            // Só o nome da cor com a espessura ao lado — sem o código.
            label: formatarCorMdf(m.descricao, m.espessura) || m.descricao,
            sublabel: "",
            codigo: m.codigo,
            descricao: m.descricao,
            espessura: m.espessura,
          })),
        );
      })
      .catch(() => {})
      .finally(() => !cancelado && setMateriaisCarregando(false));

    listarFitasSelect(request)
      .then((payload) => {
        if (cancelado) return;
        // fitas-select devolve só as CORES distintas ([{cor}]) — o usuário
        // escolhe a cor; a largura é resolvida pela espessura do MDF no XLS.
        setFitas(
          unwrapLista(payload).map((f) => ({
            id: f.cor,
            cor: f.cor,
            label: f.cor,
          })),
        );
      })
      .catch(() => {})
      .finally(() => !cancelado && setFitasCarregando(false));

    return () => {
      cancelado = true;
    };
  }, [request]);

  const mudarCab = (campo, valor) => {
    setSujo(true);
    setCab((atual) => ({ ...atual, [campo]: valor }));
    setErros((e) => {
      const copia = { ...e };
      delete copia[campo === "cliente" || campo === "clienteTexto" ? "cliente" : campo];
      return copia;
    });
  };

  const mudarPeca = (indice, campo, valor) => {
    setSujo(true);
    setPecas((atual) =>
      atual.map((p, i) => (i === indice ? { ...p, [campo]: valor } : p)),
    );
    setErros((e) => {
      const copia = { ...e };
      delete copia[`p${indice}_${campo}`];
      if (campo === "motivo_categoria") {
        delete copia[`p${indice}_motivo`];
        // Trocar o motivo pode dispensar o detalhe (saiu de "Outro").
        delete copia[`p${indice}_motivo_detalhe`];
      }
      if (campo === "material_id") delete copia[`p${indice}_material`];
      return copia;
    });
  };

  const mudarPecaVarios = (indice, valores) => {
    setSujo(true);
    setPecas((atual) =>
      atual.map((p, i) => (i === indice ? { ...p, ...valores } : p)),
    );
    if (valores.material_id) {
      setErros((e) => {
        const copia = { ...e };
        delete copia[`p${indice}_material`];
        return copia;
      });
    }
  };

  // Retorna o uid da nova peça para o wizard saltar direto para ela.
  const adicionarPeca = () => {
    const nova = novaPeca();
    setSujo(true);
    setPecas((atual) => [...atual, nova]);
    return nova.uid;
  };

  const duplicarPeca = (indice) => {
    const nova = { ...pecas[indice], uid: gerarUid(), fotos: [] };
    setSujo(true);
    setPecas((atual) => {
      const proximas = [...atual];
      proximas.splice(indice + 1, 0, nova);
      return proximas;
    });
    return nova.uid;
  };

  const removerPeca = (indice) => {
    setSujo(true);
    setPecas((atual) => (atual.length > 1 ? atual.filter((_, i) => i !== indice) : atual));
    setErros({});
  };

  // Peça em branco pode ser descartada sem confirmar — é o que torna reversível
  // o clique acidental em "Adicionar outra peça" (ver decorarSaidaDoBloco).
  const pecaIntocada = (peca) => registroIntocado(peca, novaPeca());

  const descartarRascunho = () => {
    limparRascunho(tipo);
    setCab(cabecalhoVazio());
    setPecas([novaPeca()]);
    setSujo(false);
    setErros({});
    setMostrarBannerRascunho(false);
  };

  function validar() {
    const e = {};
    if (!cab.cliente) {
      e.cliente = "Selecione o cliente da lista.";
    }
    if ((tipo === "ASM" || tipo === "AST") && !cab.lider_montagem_nome.trim()) {
      e.lider_montagem_nome = "Informe o líder de montagem.";
    }
    if (tipo === "ASP" && !cab.setor) {
      e.setor = "Selecione o setor.";
    }
    pecas.forEach((p, i) => {
      if (!p.ambiente.trim()) e[`p${i}_ambiente`] = "Informe o ambiente.";
      if (!p.descricao.trim()) e[`p${i}_descricao`] = "Informe a descrição da peça.";
      if (p.natureza === "NOVA") {
        if (!p.modulo.trim()) e[`p${i}_modulo`] = "Obrigatório para peça nova.";
        if (!p.material_id) e[`p${i}_material`] = "Selecione a chapa do Cadastro de Suprimentos.";
        // Medidas: obrigatórias na peça nova (o corte precisa delas — o
        // backend rejeita sem) e dentro dos limites físicos 20–2730 × 20–1830.
        // Mão de obra não valida: medida lá é só referência, nada será cortado.
        if (parseDecimal(p.comprimento_mm) == null) {
          e[`p${i}_comprimento_mm`] = "Informe o comprimento (peça nova).";
        } else {
          const erroComprimento = validarMedidaMdf("comprimento_mm", p.comprimento_mm);
          if (erroComprimento) e[`p${i}_comprimento_mm`] = erroComprimento;
        }
        if (parseDecimal(p.largura_mm) == null) {
          e[`p${i}_largura_mm`] = "Informe a largura (peça nova).";
        } else {
          const erroLargura = validarMedidaMdf("largura_mm", p.largura_mm);
          if (erroLargura) e[`p${i}_largura_mm`] = erroLargura;
        }
      }
      if (!p.motivo_categoria) e[`p${i}_motivo`] = "Selecione o motivo.";
      const erroDetalhe = erroDetalheDoMotivo(p.motivo_categoria, p.motivo_detalhe);
      if (erroDetalhe) e[`p${i}_motivo_detalhe`] = erroDetalhe;
      if (!((parseInt(p.quantidade, 10) || 0) >= 1)) {
        e[`p${i}_quantidade`] = "Quantidade mínima: 1.";
      }
      if (contarBordasComFita(p) > 0 && !p.fita_nome.trim()) {
        e[`p${i}_fita_nome`] = "Informe o nome da fita de borda.";
      }
      if (p.usinagem && !p.usinagem_descricao.trim()) {
        e[`p${i}_usinagem_descricao`] = "Descreva a usinagem.";
      }
      if (p.pintura && !p.pintura_descricao.trim()) {
        e[`p${i}_pintura_descricao`] = "Descreva a pintura.";
      }
      if ((p.observacao || "").length > 26) {
        e[`p${i}_observacao`] = "Máximo de 26 caracteres.";
      }
    });
    return e;
  }

  function montarPayload() {
    return {
      tipo,
      num_pedido: cab.num_pedido.trim(),
      cliente_id: cab.cliente?.id || undefined,
      cliente_nome: cab.cliente?.nome || "",
      supervisor_nome: cab.supervisor_nome.trim(),
      lider_montagem_nome: tipo === "ASP" ? "" : cab.lider_montagem_nome.trim(),
      setor: tipo === "ASP" ? cab.setor : "",
      lote_producao: tipo === "ASP" ? cab.lote_producao.trim() : "",
      pecas: pecas.map((p) => ({
        ambiente: p.ambiente.trim(),
        modulo: p.modulo.trim(),
        ref_modulo: p.ref_modulo.trim(),
        descricao: p.descricao.trim(),
        natureza: p.natureza,
        motivo_categoria: p.motivo_categoria,
        motivo_detalhe: p.motivo_detalhe.trim(),
        material_id: p.material_id || undefined,
        material_nome: p.material_nome,
        material_codigo: p.material_codigo,
        comprimento_mm: parseDecimal(p.comprimento_mm),
        largura_mm: parseDecimal(p.largura_mm),
        espessura_mm: parseDecimal(p.espessura_mm),
        quantidade: parseInt(p.quantidade, 10) || 1,
        observacao: (p.observacao || "").trim(),
        usinagem: p.usinagem,
        usinagem_descricao: p.usinagem ? p.usinagem_descricao.trim() : "",
        sentido_veio: p.sentido_veio,
        pintura: p.pintura,
        pintura_descricao: p.pintura ? p.pintura_descricao.trim() : "",
        fita_frente: p.fita_frente,
        fita_traseira: p.fita_traseira,
        fita_le: p.fita_le,
        fita_ld: p.fita_ld,
        fita_nome: contarBordasComFita(p) > 0 ? p.fita_nome.trim() : "",
        fotos: p.fotos.map((f) => ({ imagem: f.imagem, legenda: f.legenda || "" })),
      })),
    };
  }

  const enviar = async () => {
    const e = validar();
    if (Object.keys(e).length) {
      setErros(e);
      toast.error("Verifique os campos destacados.");
      throw new Error("Campos inválidos.");
    }
    setErros({});
    const resposta = await criarAssistencia(request, montarPayload());
    const solicitacao = unwrapItem(resposta);
    limparRascunho(tipo);
    navigate("../comprovante", {
      state: { solicitacao, familia: "assistencia", tipo },
    });
  };

  const passos = passosAssistencia({
    tipo,
    cab,
    pecas,
    clientes,
    clientesCarregando,
    materiais,
    materiaisCarregando,
    fitas,
    fitasCarregando,
    erros,
    mudarCab,
    mudarPeca,
    mudarPecaVarios,
    adicionarPeca,
    duplicarPeca,
    removerPeca,
    pecaIntocada,
  });

  const aviso = mostrarBannerRascunho ? (
    <BannerRascunho>
      <span>Rascunho recuperado — as fotos precisam ser adicionadas de novo.</span>
      <button type="button" onClick={() => setMostrarBannerRascunho(false)}>
        Continuar
      </button>
      <button type="button" onClick={descartarRascunho}>
        Descartar
      </button>
    </BannerRascunho>
  ) : null;

  return (
    <Wizard
      titulo={tipoInfo.label}
      codigo={`Nova solicitação · ${tipo}`}
      aoSair={() => navigate("..")}
      passos={passos}
      onEnviar={enviar}
      podeEnviar={Object.keys(validar()).length === 0}
      aviso={aviso}
    />
  );
}
